import React, { useMemo, useState } from 'react'
import type { QuoteFile } from 'paintscout'
import type { QuoteItemSection } from '@paintscout/util/builder'
import { getItems, getFilesForItem, trimGroup } from '@paintscout/util/builder'
import { Grid } from '@paintscout/ui'
import type { PresentationBlockProps } from '../../'
import { useQuote, usePresentation } from '../../../context'
import { PresentationFilesGrid, ViewMediaDialog } from '../../components'
import { useClientOptions, useDialogs } from '@paintscout/ui-business'
import PicturesPlaceholder from './PicturesPlaceholder'

const SECTIONS: QuoteItemSection[] = ['bid', 'additional', 'added-options', 'pending', 'options']
const CONSUMER = 'customer'

export function isVisibleToConsumer(file: QuoteFile) {
  if (file.visibility === 'visible') return true
  if (file.visibility === 'hidden') return false
  return file.visibility === CONSUMER
}

/**
 * A live gallery of the quote's own job pictures — no configurable fields, entirely derived from
 * the quote in context. Rendered flat (not grouped per line item like the v1/FilesByItem view) so
 * it shares Gallery's own grid + lightbox (PresentationFilesGrid/ViewMediaDialog) instead of the
 * legacy FilesGrid/FilesLightboxDialog pair FilesByItem uses.
 */
export default function PicturesBlock(props: PresentationBlockProps) {
  const { WrapperComponent, isSettings, presentationIndustry } = props
  const { openDialog, dismissDialog } = useDialogs()
  const { quote } = useQuote()
  const { options } = useClientOptions()
  const { presentationLibrary } = usePresentation()
  const [, setIndex] = useState(0)

  const files = useMemo(() => {
    const view = 'area'
    const groups = Object.values(quote.groups ?? {})
    const allItems = SECTIONS.reduce(
      (items, section) => [
        ...items,
        ...getItems({
          quote,
          consumer: CONSUMER,
          section,
          view,
          options,
          overrideOptions: { showHiddenItems: true, includeAreasWithNoSubstrates: true }
        })
      ],
      []
    ).filter((item) => !groups.some((group) => group?.children?.includes(item.key)))

    const trimmedGroups = groups
      .filter((group) => SECTIONS.includes(group.section))
      .map((group) => trimGroup(group, options, quote))

    return [...allItems, ...trimmedGroups].flatMap((item) =>
      getFilesForItem({ quote, options, itemKey: item.key }).filter(isVisibleToConsumer)
    )
  }, [quote, options])

  if (!files.length && !isSettings) {
    return null
  }

  function handleViewFiles(fileIndex: number) {
    openDialog(ViewMediaDialog, {
      files: files.filter((file) => !!file?.src),
      index: fileIndex,
      onIndexChange: setIndex,
      enableDownload: !isSettings,
      onClose: dismissDialog
    })
  }

  return (
    <WrapperComponent {...props}>
      {isSettings ? (
        <PicturesPlaceholder />
      ) : (
        <Grid container spacing={3} justifyContent={'center'} alignItems={'flex-start'}>
          <Grid item xs={12}>
            <PresentationFilesGrid
              isEditable={false}
              isSettings={isSettings}
              inPresentation={true}
              justify="space-around"
              files={files}
              presentationIndustry={presentationIndustry}
              presentationLibrary={presentationLibrary}
              showDetails={false}
              onFileClick={handleViewFiles}
              fullItemHeight={true}
              filePreviewProps={{ showFirstPageOnly: true }}
            />
          </Grid>
        </Grid>
      )}
    </WrapperComponent>
  )
}
